import useClickOutside from 'lib/hooks/useClickOutside';
import moment from 'moment';
import { useState, useRef } from 'react';
import styles from './year-picker.module.scss';
import classNames from 'classnames';
import { ChevronDown, ChevronLeft, ChevronRight } from '@carbon/react/icons';

interface YearPickerProps {
  selectedYear: moment.Moment;
  setSelectedYear: (year: number) => void;
  label?: string;
  light?: boolean;
}

// This simulates Carbon components with Carbon styling
const YearPicker = (props: YearPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedYear = props.selectedYear.year();
  const [viewStartYear, setViewStartYear] = useState(selectedYear - 3); // Start of the current view

  const dropdownRef = useRef(null);
  const yearsPerPage = 12; // Number of years to show per page (3x4 grid)

  const handleSelection = (year: number) => {
    props.setSelectedYear(year);
    setIsOpen(false);
  };

  // Use a separate ref for the calendar dropdown content
  const calendarContentRef = useRef(null);

  useClickOutside({
    refs: [dropdownRef, calendarContentRef],
    onClickOutside: () => {
      setIsOpen(false);
    },
  });

  // Generate years for current view
  const getYearsForView = () => {
    return Array.from({ length: yearsPerPage }, (_, i) => viewStartYear + i);
  };

  // Navigate between pages of years
  const navigateYears = (direction: number) => {
    setViewStartYear(viewStartYear + direction * yearsPerPage);
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.light]: props.light,
      })}
    >
      {props.label && <label className={styles.label}>{props.label}</label>}
      <div
        className={classNames(styles.input, {
          [styles.focused]: isOpen,
        })}
        onClick={() => {
          // Update viewStartYear to center around the selected year when opening
          if (!isOpen) {
            setViewStartYear(
              Math.floor(selectedYear / yearsPerPage) * yearsPerPage
            );
          }
          setIsOpen(!isOpen);
        }}
        ref={dropdownRef}
      >
        <span className={styles.inputText}>{selectedYear}</span>
        <ChevronDown className={styles.icon} />
      </div>
      {isOpen && (
        <div className={styles.calendar} ref={calendarContentRef}>
          {/* Calendar header with navigation */}
          <div className={styles.header}>
            <button
              className={styles.navButton}
              onClick={() => navigateYears(-1)}
              aria-label="Previous years"
            >
              <ChevronLeft />
            </button>

            <span className={styles.yearIndicator}>
              {viewStartYear} - {viewStartYear + yearsPerPage - 1}
            </span>

            <button
              className={styles.navButton}
              onClick={() => navigateYears(1)}
              aria-label="Next years"
            >
              <ChevronRight />
            </button>
          </div>

          {/* Year picker grid */}
          <div className={styles.yearsGrid}>
            {getYearsForView().map((year) => (
              <button
                key={year}
                className={classNames(styles.yearButton, {
                  [styles.selected]: year === selectedYear,
                })}
                onClick={() => handleSelection(year)}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              className={styles.footerButton}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YearPicker;
